import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, PREDEFINED_THEMES } from '../../contexts/ThemeContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useSessionManager } from '../../hooks/useSessionManager';
import {
  LayoutDashboard, ChevronDown, ChevronRight, Tag, DollarSign, Truck, FileText,
  Receipt, Package, Users, FileBadge, ScrollText, BarChart3, ShoppingCart,
  FileQuestion, CheckSquare, Box, Boxes, PackageCheck, BookOpen,
  CreditCard, Menu, LogOut, Settings, Building2, ClipboardList,
  X, Search, Bell, Info, Eye, Monitor, Layers, Lock, Palette, CalendarClock, Globe, HelpCircle, MessageSquare, Download
} from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import NotificationsPanel from '../NotificationsPanel';
import eyvoLogoSmall from '../../assets/eyvo_logo_small.png';
import CommandPalette from '../CommandPalette';
import AboutModal from '../AboutModal';
import EulaModal from '../EulaModal';

const NAV_ITEMS = [
  { id: 'dashboard', label: 'nav.dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { id: 'atc', label: 'Command Center', icon: Monitor, path: '/atc' },
  {
    id: 'data-files', label: 'nav.dataFiles', icon: FileText, children: [
      { id: 'categories',      label: 'nav.categories',      icon: Tag,        path: '/data/categories',      perm: 'category_code' },
      { id: 'currencies',      label: 'nav.currencies',      icon: DollarSign, path: '/data/currencies',      perm: 'currency_code' },
      { id: 'delivery-points', label: 'nav.deliveryPoints',  icon: Truck,       path: '/data/delivery-points', perm: 'delivery_point' },
      { id: 'document-types',  label: 'nav.documentTypes',   icon: FileText,    path: '/data/document-types',  perm: 'document_type' },
      { id: 'invoice-points',  label: 'nav.invoicePoints',   icon: Receipt,     path: '/data/invoice-points',  perm: 'invoice_point' },
      { id: 'items',           label: 'nav.items',           icon: Package,     path: '/data/items',           perm: 'items' },
      { id: 'suppliers',       label: 'nav.suppliers',       icon: Users,       path: '/data/suppliers',       perm: 'supplier' },
      { id: 'customers',       label: 'Customers',           icon: Users,       path: '/customers',          perm: 'customers' },
      { id: 'terms',           label: 'nav.terms',           icon: ScrollText,  path: '/data/terms',           perm: 'terms_conditions' },
      { id: 'group-items',     label: 'Group Items',          icon: Layers,      path: '/data/group-items' },
    ]
  },
  { id: 'account-codes', label: 'nav.accountCodes', icon: BarChart3, path: '/account-codes' },
  { id: 'contracts',    label: 'Contracts',        icon: FileBadge, path: '/data/contracts', perm: 'supplier_contracts', module: 'contracts' },
  { id: 'requests',      label: 'nav.requests',     icon: ClipboardList, path: '/requests',         perm: 'fn_requisitions', module: 'requisitions' },
  { id: 'rfq',          label: 'nav.rfq',          icon: FileQuestion,  path: '/rfq',              perm: 'fn_rfx', module: 'rfq' },
  { id: 'orders',        label: 'nav.orders',       icon: ShoppingCart,  path: '/orders',           perm: 'fn_purchase_orders', module: 'orders' },
  { id: 'sales-orders', label: 'Sales Orders',     icon: ShoppingCart,  path: '/sales-orders',     perm: 'fn_sales_orders', module: 'sales_orders' },
  { id: 'approvals',     label: 'nav.approvals',    icon: CheckSquare,   path: '/approvals' },
  { id: 'asset-register',label: 'nav.assetRegister',icon: Box,           path: '/assets',          perm: 'fn_asset', module: 'assets' },
  { id: 'invoice-match', label: 'nav.invoiceMatching',icon: Receipt,     path: '/invoices', perm: 'fn_invoice_payments', module: 'invoicing' },
  { id: 'inventory',     label: 'nav.inventory',    icon: Boxes,         path: '/inventory',        perm: 'fn_inventory', module: 'inventory' },
  { id: 'goods-received',label: 'nav.goodsReceived',icon: PackageCheck,  path: '/goods-received', module: 'goods_receiving' },
  { id: 'recipe',        label: 'nav.recipe',       icon: BookOpen,      path: '/recipe',           perm: 'fn_recipe', module: 'recipe' },
  { id: 'expenses',      label: 'nav.expenses',     icon: CreditCard,    path: '/expenses',         perm: 'fn_expense', module: 'expenses' },
  { id: 'scheduled-reports', label: 'Scheduled Reports', icon: CalendarClock, path: '/scheduled-reports' },
  { id: 'activity-log', label: 'Activity Log', icon: Eye, path: '/activity-log' },
  { id: 'user-guides', label: 'User Guides', icon: BookOpen, path: '/guides' },
];

function SystemClock({ tz }) {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => {
      try {
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-GB', { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric' });
        const timeStr = now.toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
        const tzShort = now.toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop();
        setTime(`${dateStr}  ${timeStr} ${tzShort}`);
      } catch { setTime(new Date().toLocaleString()); }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [tz]);
  return (
    <p className="text-[10px] font-medium tracking-wide font-mono" style={{ color: 'hsl(var(--sidebar-foreground) / 0.5)' }}>
      {time}
    </p>
  );
}

function TimezoneBadge({ tz }) {
  const [abbr, setAbbr] = useState('');
  useEffect(() => {
    try {
      const short = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop();
      setAbbr(short);
    } catch { setAbbr(''); }
  }, [tz]);
  if (!abbr) return null;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="hidden md:inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/50 px-2.5 py-1 text-[10px] font-semibold text-muted-foreground tracking-wide cursor-default select-none" data-testid="timezone-chip">
            <Globe className="h-3 w-3 opacity-60" />
            {abbr}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <p>System timezone: {tz}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function GlobalHelpButton() {
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <div className="relative" ref={menuRef}>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setOpen(!open)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors shadow-sm"
                data-testid="global-help-btn"
              >
                <span className="text-base font-bold leading-none">?</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs"><p>Help &amp; Support</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {open && (
          <div className="absolute right-0 top-full mt-1 z-50 w-60 bg-popover border rounded-lg shadow-xl py-1 animate-in fade-in-0 zoom-in-95" data-testid="global-help-menu">
            <button onClick={() => { setOpen(false); setChatOpen(true); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-accent transition-colors" data-testid="global-help-ai-chat">
              <MessageSquare className="h-4 w-4 text-blue-500 shrink-0" />
              <div><p className="font-medium">AI Chat Support</p><p className="text-[10px] text-muted-foreground">Ask anything about Eyvo</p></div>
            </button>
            <button onClick={() => { setOpen(false); navigate('/guides'); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-accent transition-colors" data-testid="global-help-guides">
              <BookOpen className="h-4 w-4 text-violet-500 shrink-0" />
              <div><p className="font-medium">User Guides</p><p className="text-[10px] text-muted-foreground">Browse all module guides</p></div>
            </button>
            <button onClick={() => { setOpen(false); window.open('/guides/general', '_blank'); setTimeout(() => {}, 1500); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-accent transition-colors" data-testid="global-help-pdf">
              <Download className="h-4 w-4 text-emerald-500 shrink-0" />
              <div><p className="font-medium">Download System Guide PDF</p><p className="text-[10px] text-muted-foreground">General system overview</p></div>
            </button>
          </div>
        )}
      </div>

      {chatOpen && <GlobalAIChat onClose={() => setChatOpen(false)} />}
    </>
  );
}

function GlobalAIChat({ onClose }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm the Eyvo AI Assistant. Ask me anything about the platform — how features work, where to find things, or best practices." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [humanRequested, setHumanRequested] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await api.post('/api/help-chat/ask', { message: text, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: "I'm sorry, I couldn't process your request right now. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="global-ai-chat-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-md bg-background shadow-2xl flex flex-col animate-in slide-in-from-right-10 duration-300">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <div><p className="text-sm font-semibold">Eyvo AI Assistant</p><p className="text-[10px] opacity-80">Powered by AI</p></div>
          </div>
          <button onClick={onClose} className="h-7 w-7 flex items-center justify-center rounded hover:bg-white/20"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="global-chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0"><MessageSquare className="h-3.5 w-3.5 text-blue-600" /></div>}
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm'}`}>
                <div className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{
                  __html: m.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/`(.*?)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:11px">$1</code>').replace(/\n/g, '<br/>')
                }} />
              </div>
            </div>
          ))}
          {loading && <div className="flex gap-2"><div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0"><MessageSquare className="h-3.5 w-3.5 text-blue-600" /></div><div className="bg-muted rounded-xl px-3 py-2 text-sm text-muted-foreground">Thinking...</div></div>}
          <div ref={bottomRef} />
        </div>
        {!humanRequested && messages.length > 3 && (
          <div className="px-4 pb-2">
            <button onClick={() => { setHumanRequested(true); setMessages(prev => [...prev, { role: 'assistant', text: "Your request for human support has been noted. A member of the Eyvo support team will follow up with you via email." }]); }}
              className="w-full text-xs text-muted-foreground hover:text-primary py-1.5 border border-dashed rounded-lg transition-colors" data-testid="request-human-support">
              Can't find what you need? Request human support
            </button>
          </div>
        )}
        <div className="border-t p-3 flex items-center gap-2">
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Ask about Eyvo..." className="flex-1 text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" disabled={loading} data-testid="global-chat-input" />
          <button onClick={sendMessage} disabled={!input.trim() || loading}
            className="h-9 w-9 flex items-center justify-center rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" data-testid="global-chat-send">
            <Search className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children, accountSegments: propSegments = [] }) {
  const { t } = useTranslation();
  const { user, logout, systemSettings } = useAuth();
  const { canRead, getAccess, isAdmin } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Session management — heartbeat + idle timeout
  useSessionManager();

  const [isMobile, setIsMobile]         = useState(() => window.innerWidth < 768);
  const [isLaptop, setIsLaptop]         = useState(() => window.innerWidth >= 768 && window.innerWidth < 1280);
  const [collapsed, setCollapsed]       = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [openGroups, setOpenGroups]     = useState(() => {
    // Auto-expand only the group containing the current URL path
    const path = window.location.pathname;
    const initial = {};
    if ((path.startsWith('/data/') || path.startsWith('/data')) && !path.startsWith('/data/contracts')) initial['data-files'] = true;
    if (path.startsWith('/account-codes')) initial['account-codes'] = true;
    return initial;
  });
  const [accountSegments, setAccountSegments] = useState(propSegments);
  const segmentsLoaded = React.useRef(false);
  const [cmdOpen, setCmdOpen]           = useState(false);
  const [aboutOpen, setAboutOpen]       = useState(false);
  const [eulaOpen, setEulaOpen]         = useState(false);
  const [themeOpen, setThemeOpen]       = useState(false);
  const { themeId, setTheme, updateCustomColor, customColors, currentTheme } = useTheme();
  const [photoVersion, setPhotoVersion] = useState(1); // bumped after every upload
  const [appVersion, setAppVersion]     = useState(null);

  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const [activeSiteId, setActiveSiteId] = useState(user?.active_site_id || '');


  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  // Stable URL — only changes when user changes OR when photoVersion is bumped after upload
  const photoUrl = user?.user_id
    ? `${BACKEND_URL}/api/users/${user.user_id}/profile-photo?v=${photoVersion}`
    : null;

  // Listen for profile photo update events dispatched by ProfilePage after a successful upload
  useEffect(() => {
    const handler = (e) => setPhotoVersion(e.detail?.version || Date.now());
    window.addEventListener('profile-photo-updated', handler);
    return () => window.removeEventListener('profile-photo-updated', handler);
  }, []);

  useEffect(() => {
    const h = () => {
      setIsMobile(window.innerWidth < 768);
      setIsLaptop(window.innerWidth >= 768 && window.innerWidth < 1280);
    };
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Auto-expand sidebar group when navigating to a child route
  useEffect(() => {
    const path = location.pathname;
    const dataFilePaths = ['/data/', '/customers'];
    if (dataFilePaths.some(p => path.startsWith(p)) && !path.startsWith('/data/contracts')) {
      setOpenGroups(prev => prev['data-files'] ? prev : { ...prev, 'data-files': true });
    }
    if (path.startsWith('/account-codes')) {
      setOpenGroups(prev => prev['account-codes'] ? prev : { ...prev, 'account-codes': true });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (propSegments.length > 0) { setAccountSegments(propSegments); segmentsLoaded.current = true; return; }
    if (segmentsLoaded.current) return;
    segmentsLoaded.current = true;
    import('../../services/api').then(({ default: api }) => {
      Promise.all([
        api.get('/api/account-codes/segments').catch(() => ({ data: [] })),
        api.get('/api/system/version').catch(() => ({ data: {} })),
        api.get('/api/health').catch(() => ({ data: {} })),
        api.get('/api/system/company-logo/file', { responseType: 'blob' }).catch(() => null),
        api.get('/api/sites/module/status').catch(() => ({ data: { enabled: false } })),
        api.get('/api/sites').catch(() => ({ data: { data: [] } })),
      ]).then(([segRes, verRes, healthRes, logoRes, sitesStatusRes, sitesRes]) => {
        setAccountSegments((segRes.data || []).filter(s => s.active));
        if (verRes.data) setAppVersion({ ...verRes.data, db_name: healthRes?.data?.db_name || '' });
        if (logoRes?.data) setCompanyLogoUrl(URL.createObjectURL(logoRes.data));
        if (sitesStatusRes.data.enabled) {
          const userSites = user?.allowed_sites || [];
          const allSites = sitesRes.data.data || [];
          const filtered = userSites.length > 0 ? allSites.filter(s => userSites.includes(s.site_id) && s.active) : allSites.filter(s => s.active);
          setAvailableSites(filtered);
          if (!activeSiteId && filtered.length > 0) setActiveSiteId(user?.active_site_id || filtered[0]?.site_id || '');
        }
      });
    });
  }, [propSegments]); // eslint-disable-line

  // Cmd/Ctrl+K opens command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const toggleGroup = (id) => setOpenGroups(p => ({ ...p, [id]: !p[id] }));
  const isActive   = (path) => location.pathname === path;
  const isGroupActive = (children) => children?.some(c => location.pathname.startsWith(c.path));
  const handleToggle  = () => isMobile ? setMobileOpen(v => !v) : setCollapsed(v => !v);
  const isCollapsed   = isMobile ? false : (isLaptop ? true : collapsed);
  const sidebarWidth  = isMobile ? 272 : (isCollapsed ? 72 : 272);
  const handleLogout  = async () => { await logout(); navigate('/login'); };

  const renderNavItem = (item) => {
    // Account Codes — dynamic segments as children
    if (item.id === 'account-codes' && accountSegments.length > 0) {
      const open = openGroups['account-codes'];
      const isACActive = location.pathname.startsWith('/account-codes');
      return (
        <div key={item.id}>
          <button
            className={`sidebar-item w-full text-left ${isACActive ? 'active' : ''}`}
            onClick={() => toggleGroup('account-codes')}
            aria-expanded={open}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
            {!isCollapsed && (<>
              <span className="flex-1 truncate">{t(item.label)}</span>
              {open ? <ChevronDown className="h-3 w-3 opacity-50" /> : <ChevronRight className="h-3 w-3 opacity-50" />}
            </>)}
          </button>
          {open && !isCollapsed && (
            <div className="ml-2 mt-0.5 border-l pl-2" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
              {accountSegments.map(seg => {
                const acPermMap = {1:'ac_account',2:'ac_restaurant',3:'ac_department_code',4:'ac_gl',5:'ac_property_code',6:'ac_nominal_code',7:'ac_cost_centre'};
                const acPerm = acPermMap[seg.segment_number];
                const acAccess = acPerm ? getAccess(acPerm) : 'read_write';
                const noAccess = acAccess === 'no_access';
                return noAccess ? (
                  <div key={seg.segment_number} className="sidebar-item opacity-40 cursor-not-allowed">
                    <span className="truncate text-xs">{seg.label}</span>
                    <Lock className="h-3 w-3 flex-shrink-0 text-red-400 ml-auto" aria-label="No access" title="No access" />
                  </div>
                ) : (
                  <Link key={seg.segment_number} to={`/account-codes/${seg.segment_number}`}
                    className={`sidebar-item ${location.pathname === `/account-codes/${seg.segment_number}` ? 'active' : ''}`}>
                    <span className="truncate text-xs">{seg.label}</span>
                    {acAccess === 'read_only' && <Eye className="h-3 w-3 flex-shrink-0 text-amber-500 ml-auto" aria-label="Read only" title="Read only" />}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (item.children) {
      const open = openGroups[item.id];
      const groupActive = isGroupActive(item.children);
      return (
        <div key={item.id}>
          <button
            className={`sidebar-item w-full text-left ${groupActive ? 'active' : ''}`}
            onClick={() => toggleGroup(item.id)}
            aria-expanded={open}
          >
            <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
            {!isCollapsed && (<>
              <span className="flex-1 truncate">{t(item.label)}</span>
              {open ? <ChevronDown className="h-3 w-3 opacity-50" /> : <ChevronRight className="h-3 w-3 opacity-50" />}
            </>)}
          </button>
          {open && !isCollapsed && (
            <div className="ml-2 mt-0.5 border-l pl-2" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
              {item.children.map(child => {
                const childAccess = child.perm ? getAccess(child.perm) : 'read_write';
                const noAccess = childAccess === 'no_access';
                return noAccess ? (
                  <div key={child.id} className="sidebar-item opacity-40 cursor-not-allowed">
                    <child.icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                    <span className="truncate text-xs">{t(child.label)}</span>
                    <Lock className="h-3 w-3 flex-shrink-0 text-red-400 ml-auto" aria-label="No access" title="No access" />
                  </div>
                ) : (
                  <Link key={child.id} to={child.path}
                    className={`sidebar-item ${isActive(child.path) ? 'active' : ''}`}
                    aria-current={isActive(child.path) ? 'page' : undefined}
                  >
                    <child.icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
                    <span className="truncate text-xs">{t(child.label)}</span>
                    {childAccess === 'read_only' && (
                      <Eye className="h-3 w-3 flex-shrink-0 text-amber-500 ml-auto" aria-label="Read only" title="Read only" />
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const itemAccess = item.perm ? getAccess(item.perm) : 'read_write';
    const noAccess = itemAccess === 'no_access';

    // Module-level visibility check — hide completely if tenant doesn't have the module
    if (item.module) {
      const tenantModules = Array.isArray(systemSettings?.tenant_modules) ? systemSettings.tenant_modules : [];
      const enabledModules = Array.isArray(systemSettings?.enabled_modules) ? systemSettings.enabled_modules : [];
      const allModules = [...tenantModules, ...enabledModules];
      if (!allModules.includes(item.module)) return null;
    }

    if (noAccess) {
      return (
        <div key={item.id} className="sidebar-item opacity-40 cursor-not-allowed">
          <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
          {!isCollapsed && <>
            <span className="truncate">{t(item.label)}</span>
            <Lock className="h-3 w-3 flex-shrink-0 text-red-400 ml-auto" aria-label="No access" title="No access" />
          </>}
        </div>
      );
    }

    return (
      <TooltipProvider key={item.id}>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <Link to={item.path}
              className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden />
              {!isCollapsed && <>
                <span className="truncate">{t(item.label)}</span>
                {itemAccess === 'read_only' && (
                  <Eye className="h-3 w-3 flex-shrink-0 text-amber-500 ml-auto" aria-label="Read only" title="Read only" />
                )}
              </>}
            </Link>
          </TooltipTrigger>
          {isCollapsed && <TooltipContent side="right">{t(item.label)}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Mobile backdrop */}
      {isMobile && mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar-nav flex flex-col h-full flex-shrink-0 transition-[width,transform] duration-200 ease-in-out ${
          isMobile
            ? `fixed inset-y-0 left-0 z-40 ${mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}`
            : 'relative'
        }`}
        style={{ width: sidebarWidth }}
        aria-label="Main navigation"
        aria-hidden={isMobile && !mobileOpen}
      >
        {/* Logo area */}
        <div className="flex items-center gap-2.5 px-4 py-3.5 border-b" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          <img src={eyvoLogoSmall} alt="Eyvo eProcurement"
            className="flex-shrink-0"
            style={{ height: isCollapsed ? '26px' : '32px', width: 'auto', filter: 'brightness(0) invert(1)', transition: 'height 0.2s' }}
          />
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-white/50 text-[10px] leading-tight tracking-wide uppercase">eProcurement</p>
            </div>
          )}
          {isMobile && (
            <button onClick={() => setMobileOpen(false)}
              className="ml-auto p-1 rounded text-white/60 hover:text-white hover:bg-white/10"
              aria-label="Close navigation">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5" aria-label="Navigation">
          {NAV_ITEMS.map(item => {
            // For items with children, pass all children (don't filter)
            if (item.children) {
              return renderNavItem(item);
            }
            return renderNavItem(item);
          })}
        </nav>

        {/* Footer */}
        <div className="px-4 py-3 border-t" style={{ borderColor: 'hsl(var(--sidebar-border))' }}>
          {!isCollapsed ? (
            <div className="space-y-1">
              <SystemClock tz={systemSettings?.timezone || 'America/Los_Angeles'} />
              <p className="text-[10px] font-medium tracking-wide" style={{ color: 'hsl(var(--sidebar-foreground) / 0.38)' }}>
                SYSTEM CURRENCY: {systemSettings?.system_currency || 'USD'}
              </p>
              {appVersion?.db_name && (
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
                  <p className="text-[10px] font-mono" style={{ color: 'hsl(var(--sidebar-foreground) / 0.38)' }}>{appVersion.db_name}</p>
                </div>
              )}
            </div>
          ) : (
            <DollarSign className="h-3.5 w-3.5 mx-auto" style={{ color: 'hsl(var(--sidebar-foreground) / 0.38)' }} />
          )}
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="eyvo-topbar" role="banner">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={handleToggle}
              aria-label={isMobile ? (mobileOpen ? 'Close menu' : 'Open menu') : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
              className="h-8 w-8 p-0"
              data-testid="sidebar-toggle">
              <Menu className="h-4 w-4" />
            </Button>

            {/* Company logo + name */}
            <div className="hidden md:flex items-center gap-2">
              {companyLogoUrl ? (
                <img src={companyLogoUrl} alt="" className="h-6 w-auto object-contain flex-shrink-0" />
              ) : (
                <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-foreground/80">{systemSettings?.company_name || 'Eyvo eProcurement'}</span>
            </div>

            {/* Command palette trigger */}
            <button
              onClick={() => setCmdOpen(true)}
              className="hidden md:flex items-center gap-2 h-8 px-3 rounded-lg border border-input bg-background text-sm text-muted-foreground hover:border-ring hover:text-foreground transition-colors"
              aria-label="Open command palette (Ctrl+K)"
              data-testid="command-palette-trigger"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs">Search...</span>
              <kbd className="ml-2 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono">⌘K</kbd>
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <TimezoneBadge tz={systemSettings?.timezone || 'America/Los_Angeles'} />
            <GlobalHelpButton />
            <NotificationsPanel />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2 h-8 px-2" data-testid="user-menu-trigger">
                  {/* Avatar — photo if uploaded, otherwise initials */}
                  <UserAvatar user={user} photoUrl={photoUrl} size={28} />
                  <div className="hidden md:block text-left">
                    <p className="text-xs font-medium leading-none">{user?.first_name} {user?.last_name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 capitalize">{user?.user_type}</p>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 border-b mb-1 flex items-center gap-2.5">
                  <UserAvatar user={user} photoUrl={photoUrl} size={32} />
                  <div>
                    <p className="text-xs font-semibold">{user?.first_name} {user?.last_name}</p>
                    <p className="text-[11px] text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/system-admin')}>
                    <Settings className="h-4 w-4 mr-2" /> System Administration
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setAboutOpen(true)}>
                  <Info className="h-4 w-4 mr-2" /> About
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setEulaOpen(true)}>
                  <ScrollText className="h-4 w-4 mr-2" /> {t('term.softwareLicence')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <Settings className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setThemeOpen(true)}>
                  <Palette className="h-4 w-4 mr-2" /> Theme
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main id="main-content" className="flex-1 overflow-auto bg-background">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            {children}
          </div>
          
          {/* Footer */}
          {appVersion && (
            <footer className="border-t bg-muted/30 px-4 md:px-6 lg:px-8 py-3 mt-8">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <p>© {new Date().getFullYear()} Eyvo eProcurement. All rights reserved.</p>
                <p className="flex items-center gap-2">
                  <span className="font-mono">v{appVersion.version}</span>
                  <span className="text-[10px]">•</span>
                  <span>Published {new Date(appVersion.deployed_at).toLocaleString('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}</span>
                  {appVersion.db_host && (
                    <>
                      <span className="text-[10px]">•</span>
                      <span className="font-mono text-[10px] text-muted-foreground/60">{appVersion.db_host}{appVersion.db_name ? ` / ${appVersion.db_name}` : ''}</span>
                    </>
                  )}
                </p>
              </div>
            </footer>
          )}
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      {/* About Modal */}
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />

      {/* EULA Modal */}
      <EulaModal open={eulaOpen} onClose={() => setEulaOpen(false)} />

      {/* Theme Picker */}
      {themeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setThemeOpen(false)}
          role="dialog" aria-modal="true" aria-label="Appearance Settings"
          onKeyDown={e => { if (e.key === 'Escape') setThemeOpen(false); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="font-bold text-sm flex items-center gap-2"><Palette className="w-4 h-4 text-indigo-600" /> Appearance</h2>
              <button onClick={() => setThemeOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Close theme picker"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">Choose a predefined colour theme or create your own custom colours.</p>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(PREDEFINED_THEMES).filter(([id]) => id !== 'custom').map(([id, t]) => (
                  <button key={id} onClick={() => setTheme(id)}
                    className={`border-2 rounded-lg p-3 text-left transition-colors ${themeId === id ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex gap-1 mb-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: t.sidebar }} />
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: t.background }} />
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: t.accent }} />
                    </div>
                    <p className="text-xs font-medium">{t.name}</p>
                  </button>
                ))}
                <button onClick={() => setTheme('custom')}
                  className={`border-2 rounded-lg p-3 text-left transition-colors ${themeId === 'custom' ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-dashed border-slate-300 hover:border-slate-400'}`}>
                  <div className="flex gap-1 mb-2">
                    <div className="w-4 h-4 rounded bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400" />
                    <div className="w-4 h-4 rounded bg-gradient-to-br from-green-400 via-yellow-400 to-red-400" />
                  </div>
                  <p className="text-xs font-medium">Custom</p>
                </button>
              </div>

              {themeId === 'custom' && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h3 className="text-xs font-semibold text-slate-700">Custom Colours</h3>
                  {[
                    { key: 'background', label: 'Page Background' },
                    { key: 'sidebar', label: 'Sidebar' },
                    { key: 'sidebarText', label: 'Sidebar Text' },
                    { key: 'accent', label: 'Accent / Primary' },
                    { key: 'headerBg', label: 'Header Background' },
                    { key: 'cardBg', label: 'Card Background' },
                    { key: 'textPrimary', label: 'Primary Text' },
                    { key: 'textSecondary', label: 'Secondary Text' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-xs text-slate-600">{label}</span>
                      <div className="flex items-center gap-2">
                        <input type="color" value={currentTheme[key] || '#ffffff'}
                          onChange={e => updateCustomColor(key, e.target.value)}
                          className="w-8 h-8 rounded border cursor-pointer" />
                        <span className="text-[10px] font-mono text-slate-400 w-16">{currentTheme[key]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── UserAvatar — profile photo or initials fallback ──────────────────────────
function UserAvatar({ user, photoUrl, size = 28 }) {
  const [imgError, setImgError] = React.useState(false);
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;
  const s = `${size}px`;

  // Reset error state whenever the URL changes (e.g. after a new photo is uploaded)
  React.useEffect(() => {
    setImgError(false);
  }, [photoUrl]);

  if (photoUrl && !imgError) {
    return (
      <img src={photoUrl} alt={`${user?.first_name} ${user?.last_name}`}
        style={{ width: s, height: s, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        onError={() => setImgError(true)} />
    );
  }
  return (
    <div className="bg-primary flex items-center justify-center text-white font-semibold flex-shrink-0"
      style={{ width: s, height: s, borderRadius: '50%', fontSize: `${Math.round(size * 0.38)}px` }}>
      {initials}
    </div>
  );
}
